import { ArrowLeft } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useAdminPlayer } from './api';
import { DASH, DataTable, DataTableShell, formatTimestamp } from './shared';

interface PlayerDetailPageProps {
  playerId: string;
  onBack: () => void;
}

export function PlayerDetailPage({ playerId, onBack }: PlayerDetailPageProps) {
  const { data, isLoading, error } = useAdminPlayer(playerId);
  const player = data?.data;

  if (isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading player...
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="space-y-4">
        <Button type="button" variant="ghost" size="sm" className="px-0" onClick={onBack}>
          <ArrowLeft />
          Back to players
        </Button>
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load player'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Button type="button" variant="ghost" size="sm" className="px-0" onClick={onBack}>
        <ArrowLeft />
        Back to players
      </Button>

      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="text-2xl font-semibold tracking-normal">{player.name}</h2>
          <Badge variant="outline">{player.position}</Badge>
          <Badge variant={player.is_active ? 'default' : 'outline'}>
            {player.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{player.team_name}</span>
          <span className="font-mono">{player.external_id}</span>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        <MetricCard label="Squads" value={player.usage.squad_count.toLocaleString()} />
        <MetricCard label="Competitions" value={player.usage.competition_count.toLocaleString()} />
        <MetricCard
          label="Active comps"
          value={player.usage.active_competition_count.toLocaleString()}
        />
        <MetricCard label="Starters" value={player.usage.starter_count.toLocaleString()} />
        <MetricCard label="Captains" value={player.usage.captain_count.toLocaleString()} />
        <MetricCard label="Price" value={player.cost.toString()} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Squad usage</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTableShell
            empty={player.usage.recent_squads.length === 0}
            emptyLabel="Not used in any squads yet"
          >
            <DataTable
              rows={player.usage.recent_squads}
              rowKey={(row) => row.squad_id}
              columns={[
                {
                  key: 'created_at',
                  label: 'Added at',
                  render: (row) => formatTimestamp(row.created_at),
                },
                {
                  key: 'competition',
                  label: 'Competition',
                  render: (row) => (
                    <div className="flex flex-col">
                      <span className="font-medium">{row.competition_title}</span>
                      <span className="text-xs text-muted-foreground">
                        {row.competition_status}
                      </span>
                    </div>
                  ),
                },
                {
                  key: 'agent',
                  label: 'Agent',
                  render: (row) => (
                    <div className="flex flex-col">
                      <span>{row.agent_name}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {row.agent_id}
                      </span>
                    </div>
                  ),
                },
                {
                  key: 'role',
                  label: 'Role',
                  render: (row) =>
                    row.is_captain ? (
                      <Badge>Captain</Badge>
                    ) : row.is_starter ? (
                      <Badge variant="outline">Starter</Badge>
                    ) : (
                      <Badge variant="outline">Bench {row.bench_order ?? DASH}</Badge>
                    ),
                },
              ]}
            />
          </DataTableShell>
        </CardContent>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[0.7fr,1.3fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Scoring summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <DetailRow label="Games played" value={player.summary.games_played} />
            <DetailRow label="Goals" value={player.summary.goals} />
            <DetailRow label="Assists" value={player.summary.assists} />
            <DetailRow label="Minutes" value={player.summary.minutes_played} />
            <DetailRow label="Total points" value={player.summary.total_points} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent scoring</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTableShell
              empty={player.recent_scores.length === 0}
              emptyLabel="No scoring rows yet"
            >
              <DataTable
                rows={player.recent_scores}
                rowKey={(row) => row.fixture_id}
                columns={[
                  {
                    key: 'round',
                    label: 'Round',
                    render: (row) => <span className="font-mono">R{row.effective_round}</span>,
                  },
                  {
                    key: 'match',
                    label: 'Match',
                    render: (row) =>
                      row.is_home
                        ? `${row.home_team} vs ${row.away_team}`
                        : `${row.home_team} vs ${row.away_team}`,
                  },
                  {
                    key: 'minutes',
                    label: 'Min',
                    align: 'right',
                    render: (row) => row.minutes_played,
                  },
                  {
                    key: 'goals',
                    label: 'G',
                    align: 'right',
                    render: (row) => row.goals,
                  },
                  {
                    key: 'assists',
                    label: 'A',
                    align: 'right',
                    render: (row) => row.assists,
                  },
                  {
                    key: 'points',
                    label: 'Pts',
                    align: 'right',
                    render: (row) => row.total_points,
                  },
                ]}
              />
            </DataTableShell>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-medium tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="grid gap-2 text-sm md:grid-cols-[130px,1fr]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
